import React, { useState, useEffect } from 'react';
import api from '../../api';
import styles from './AdminManualLockCreator.module.css';

const AdminManualLockCreator = () => {
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pickType, setPickType] = useState('');
  const [lockLine, setLockLine] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [gameSearchTerm, setGameSearchTerm] = useState('');
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState([]);

  // Filter games based on search term
  const filteredGames = games.filter(game => {
    const searchTerm = gameSearchTerm.toLowerCase();
    const homeTeam = (game.home_team_name || game.homeTeamName || '').toLowerCase();
    const awayTeam = (game.away_team_name || game.awayTeamName || '').toLowerCase();
    return homeTeam.includes(searchTerm) || awayTeam.includes(searchTerm);
  });

  // Fetch games for selected week
  const fetchGamesForWeek = async (week) => {
    try {
      const response = await api.get(`/admin/games?week=${week}`);
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      fetchGamesForWeek(selectedWeek);
    }
  }, [selectedWeek]);

  useEffect(() => {
    // Fetch available weeks from the current season
    const fetchWeeks = async () => {
      try {
        const response = await api.get('/admin/weeks');
        setAvailableWeeks(response.data);
      } catch (error) {
        console.error('Error fetching weeks:', error);
      }
    };

    fetchWeeks();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isGameDropdownOpen && !event.target.closest(`.${styles.gameSearchContainer}`)) {
        setIsGameDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGameDropdownOpen]);

  const handleGameSelect = (gameId) => {
    setSelectedGame(gameId);
    setIsGameDropdownOpen(false);
    setGameSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const lockData = {
      userId: selectedUser,
      gameId: selectedGame,
      pickType: pickType,
      weekNumber: selectedWeek,
      lockLine: parseFloat(lockLine),
      ...(pickType !== 'no_pick' && { createdAt: createdAt || new Date().toISOString() }),
    };

    try {
      await api.post('/admin/manual-lock', lockData);
      // Reset form
      setSelectedGame(null);
      setPickType('');
      setLockLine('');
      setCreatedAt('');
      alert('Lock created successfully');
    } catch (error) {
      console.error('Error creating manual lock:', error);
      alert('Error creating lock: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Find selected game details
  const selectedGameDetails = games.find(game => game.id === selectedGame);

  const gameListId = 'gameOptionsList';

  return (
    <div className={styles.manualLockCreator}>
      <h2>Create Manual Lock</h2>
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="week">Week:</label>
          <select
            id="week"
            value={selectedWeek || ''}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            required
          >
            <option value="">Select Week</option>
            {availableWeeks.map(week => (
              <option key={week} value={week}>Week {week}</option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="user">User:</label>
          <select
            id="user"
            value={selectedUser || ''}
            onChange={(e) => setSelectedUser(e.target.value)}
            required
          >
            <option value="">Select User</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.username}</option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="gameSearch">Game:</label>
          <div className={styles.gameSearchContainer}>
            <input
              id="gameSearch"
              type="text"
              value={gameSearchTerm}
              onChange={(e) => {
                setGameSearchTerm(e.target.value);
                setIsGameDropdownOpen(true);
              }}
              onClick={() => setIsGameDropdownOpen(true)}
              placeholder="Search for a team..."
              className={styles.gameSearchInput}
              role="combobox"
              aria-expanded={isGameDropdownOpen}
              aria-controls={gameListId}
              aria-autocomplete="list"
            />
            {selectedGameDetails && (
              <div className={styles.selectedGame}>
                Selected: {selectedGameDetails.away_team_name || selectedGameDetails.awayTeamName} @
                {selectedGameDetails.home_team_name || selectedGameDetails.homeTeamName}
              </div>
            )}
            {isGameDropdownOpen && filteredGames.length > 0 && (
              <datalist
                id={gameListId}
                className={styles.gameDropdown}
              >
                {filteredGames.map(game => (
                  <option
                    key={game.id}
                    value={`${game.away_team_name || game.awayTeamName} @ ${game.home_team_name || game.homeTeamName}`}
                    onClick={() => handleGameSelect(game.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleGameSelect(game.id);
                      }
                    }}
                    data-game-id={game.id}
                    aria-selected={game.id === selectedGame}
                  />
                ))}
              </datalist>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="pickType">Pick Type:</label>
          <select
            id="pickType"
            value={pickType}
            onChange={(e) => setPickType(e.target.value)}
            required
          >
            <option value="">Select Pick Type</option>
            {selectedGameDetails ? (
              <>
                <option value="home">{selectedGameDetails.home_team_name || selectedGameDetails.homeTeamName}</option>
                <option value="away">{selectedGameDetails.away_team_name || selectedGameDetails.awayTeamName}</option>
                <option value="over">Over</option>
                <option value="under">Under</option>
                <option value="bye">Bye Week</option>
                <option value="no_pick">No Pick</option>
              </>
            ) : (
              <>
                <option value="home">Home</option>
                <option value="away">Away</option>
                <option value="over">Over</option>
                <option value="under">Under</option>
                <option value="bye">Bye Week</option>
                <option value="no_pick">No Pick</option>
              </>
            )}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="lockLine">Lock Line:</label>
          <input
            id="lockLine"
            type="number"
            step="0.5"
            value={lockLine}
            onChange={(e) => setLockLine(e.target.value)}
            required={!['bye', 'no_pick'].includes(pickType)}
            disabled={['bye', 'no_pick'].includes(pickType)}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="createdAt">Created At:</label>
          <input
            id="createdAt"
            type="datetime-local"
            value={createdAt}
            onChange={(e) => setCreatedAt(e.target.value)}
            disabled={pickType === 'no_pick'}
          />
        </div>

        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Lock'}
        </button>
      </form>
    </div>
  );
};

export default AdminManualLockCreator;
